import React, { useEffect, useMemo } from 'react';
import {
  BankOutlined,
  DownOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Avatar, Dropdown, Space, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth, useLogout } from '@/features/auth';
import {
  OrganizationMembershipResponse,
  UserAccountResponse,
  useGetMyAccount,
} from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { StorageKeys } from '@/shared/types';
import { Col, Row, useFullScreenLoaderContext } from '@/shared/ui';
import { apiCookies } from '@/shared/utils';

const enum MenuKeys {
  OrganizationSwitch = 'organizationSwitch',
  ProfileInfo = 'profileInfo',
  Organization = 'organization',
  PresonalProfile = 'presonalProfile',
  Logout = 'logout',
}

const { Text } = Typography;

export const PageLayoutSettingsMenuBtn = () => {
  const { accessToken } = useAuth();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { activeOrganization, setActiveOrganization } = useRouterContext();
  const { showLoader, hideLoader } = useFullScreenLoaderContext();

  const { data: accountData } = useGetMyAccount<UserAccountResponse>(
    {},
    { enabled: Boolean(accessToken) },
  );

  const profile = useMemo(() => accountData?.profile, [accountData]);
  const organizations = useMemo(
    () => accountData?.organizations,
    [accountData],
  );

  const currentRole = useMemo(
    () => activeOrganization?.role,
    [activeOrganization?.role],
  );

  const items: MenuProps['items'] = useMemo(
    () => [
      organizations &&
        organizations.length > 1 && {
          key: MenuKeys.OrganizationSwitch,
          label: 'Switch organization',
          children: organizations
            ?.filter((org) => org.id !== activeOrganization?.id)
            .map((org) => ({
              key: org.id,
              label: org.name,
              onClick: () => switchOrganization(org),
            })),
          style: { margin: '0px' },
        },
      {
        key: MenuKeys.ProfileInfo,
        label: (
          <Row
            style={{
              cursor: 'auto',
              pointerEvents: 'auto',
            }}
          >
            <Avatar
              icon={<UserOutlined />}
              src={profile?.avatarImage}
              style={{
                color: themeConfig[theme].colors.text,
                border: '1px solid',
                borderColor: themeConfig[theme].colors.text,
                backgroundColor: themeConfig[theme].colors.bgBase,
                fontSize: 15,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
            <Col alignItems="flex-start">
              <div>{`${profile?.firstName} ${profile?.lastName}`}</div>
              <div style={{ fontSize: 12, marginTop: -3, opacity: 0.6 }}>
                {profile?.email}
              </div>
            </Col>
          </Row>
        ),
        disabled: true,
      },
      {
        type: 'divider',
      },
      currentRole === 'admin' && {
        key: MenuKeys.Organization,
        label: (
          <Space>
            <BankOutlined style={{ height: '100%' }} />
            Manage Organization
          </Space>
        ),
      },
      {
        key: MenuKeys.PresonalProfile,
        label: (
          <Space>
            <UserOutlined style={{ height: '100%' }} />
            Personal Profile
          </Space>
        ),
      },
      {
        type: 'divider',
      },
      {
        key: MenuKeys.Logout,
        label: (
          <Space style={{ color: themeConfig[theme].colors.error }}>
            <LogoutOutlined />
            Log out
          </Space>
        ),
      },
    ],
    [profile, organizations, activeOrganization, theme],
  );

  const onClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case MenuKeys.PresonalProfile: {
        return navigate('/dashboard/profile');
      }
      case MenuKeys.Organization: {
        return navigate('/dashboard/organization');
      }
      case MenuKeys.Logout: {
        return logout();
      }

      default:
        return null;
    }
  };

  const switchOrganization = (organization: OrganizationMembershipResponse) => {
    apiCookies.set(StorageKeys.ActiveOrganization, organization.id);
    setActiveOrganization(organization);
    showLoader(Infinity);
    setTimeout(() => {
      navigate('/');
    }, 1000);
    setTimeout(() => {
      hideLoader();
    }, 1500);
  };

  useEffect(() => {
    if (activeOrganization?.id) {
      apiCookies.set(StorageKeys.ActiveOrganization, activeOrganization.id);
    }
  }, [activeOrganization]);

  return (
    <Dropdown
      className="settings-menu-button"
      menu={{ items, onClick }}
      dropdownRender={(menu) => (
        <DropdownWrapper $color={themeConfig[theme].colors.text}>
          {React.cloneElement(menu as React.ReactElement)}
        </DropdownWrapper>
      )}
      trigger={['click']}
    >
      <a onClick={(e) => e.preventDefault()}>
        <Row gap={7} style={{ height: 50 }}>
          <Row
            style={{
              background: themeConfig[theme].colors.layoutOverlayBg,
              padding: 8,
              borderRadius: 6,
            }}
          >
            <OrganizationTitle ellipsis={true}>
              {activeOrganization?.name}
            </OrganizationTitle>
            <DownOutlined
              style={{
                fontSize: 12,
                fontWeight: 'bold',
                marginTop: 2,
                color: themeConfig[theme].colors.text,
              }}
            />
          </Row>
        </Row>
      </a>
    </Dropdown>
  );
};

const DropdownWrapper = styled.div<{ $color: string }>`
  .ant-dropdown-menu-submenu.ant-dropdown-menu-submenu-vertical {
    padding: 0 !important;
  }
  li:not(.ant-dropdown-menu-item-divider),
  .ant-dropdown-menu-submenu-title {
    padding: 8px 12px !important;
  }
  .ant-dropdown-menu-item-disabled {
    color: ${({ $color }) => $color + ' !important'};
    cursor: auto !important;
  }
  .ant-dropdown-menu-item-disabled:hover {
    background-color: transparent !important;
  }
`;

const OrganizationTitle = styled(Text)`
  user-select: none;
  font-family: Inter, sans-serif !important;
  font-weight: bold;
  margin-bottom: 0px;
  font-family: 'Inter';
  whitespace: 'nowrap';
  maxwidth: 180;
  overflow: 'hidden';
`;
